import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { Calendar, Tag, ArrowRight } from "lucide-react";

export default function BlogList() {
  const [data, setData] = useState({ posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("limit", "30");
    if (activeTag) params.set("tag", activeTag);
    api.get(`/blog?${params.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [activeTag]);

  const allTags = Array.from(new Set(
    (data.posts || []).flatMap((p) => p.tags || [])
  )).slice(0, 12);

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--ink-1)" }}>
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/70">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/lessons" className="hover:text-white">Lessons</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.25em] uppercase border"
          style={{ backgroundColor: "rgba(217,4,41,0.10)", borderColor: "rgba(217,4,41,0.35)", color: "var(--brand-secondary)" }}>
          The ArmIQ Journal
        </div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-3 text-white">
          Sports science, decoded.
        </h1>
        <p className="text-base text-white/65 mt-3 max-w-2xl leading-relaxed">
          Pitching mechanics, arm-care research, and platform updates — written for coaches, athletic trainers, and ambitious athletes.
        </p>
      </section>

      {/* Tag filters */}
      {allTags.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-8 flex flex-wrap gap-2" data-testid="blog-tag-filter">
          <button onClick={() => setActiveTag(null)}
            className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${!activeTag ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200" : "border-white/10 bg-white/5 text-white/60"}`}>
            All
          </button>
          {allTags.map((t) => (
            <button key={t} onClick={() => setActiveTag(t)}
              className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${activeTag === t ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200" : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"}`}
              data-testid={`blog-tag-${t}`}>
              {t}
            </button>
          ))}
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 mt-10">
        {loading && <div className="text-white/40 py-12 text-center text-sm">Loading…</div>}
        {!loading && data.posts.length === 0 && (
          <div className="rounded-2xl ink-card p-16 text-center">
            <div className="font-display font-bold text-xl text-white">No posts yet.</div>
            <p className="text-sm text-white/55 mt-2">Check back soon — AutoSEO publishes new articles weekly.</p>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
          {data.posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`}
              className="block rounded-2xl ink-card overflow-hidden border border-transparent hover:border-cyan-400/40 transition"
              data-testid={`blog-card-${p.slug}`}>
              {p.cover_image && (
                <div className="aspect-[16/9] overflow-hidden bg-black/30">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">
                  <Calendar className="h-3 w-3" />
                  <span>{new Date(p.published_at).toLocaleDateString()}</span>
                  {p.category && <><span>·</span><span style={{ color: "var(--brand-secondary)" }}>{p.category}</span></>}
                </div>
                <h2 className="font-display font-bold text-xl text-white leading-tight mt-2">{p.title}</h2>
                {p.excerpt && <p className="text-sm text-white/60 mt-2 line-clamp-3 leading-relaxed">{p.excerpt}</p>}
                {(p.tags || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-white/5 text-white/55 border border-white/10">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold" style={{ color: "var(--brand-secondary)" }}>
                  Read article <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
